/**
 * The drawer's GUARDED close, reachable from anything drawn inside it.
 *
 * A Cancel button in a footer and the header's X must behave identically, and they would not if
 * the footer called the caller's raw `onClose` while the X went through the unsaved-work guard.
 * One function, reached from context, so there is only one way out of a drawer and it is the
 * checked one.
 *
 * ── WHY ITS OWN FILE ────────────────────────────────────────────────────────
 * `Drawer.tsx` exports a component and nothing else, which is what Fast Refresh requires: a
 * module that exports a component alongside a hook or a constant is re-evaluated wholesale on
 * every edit, and the component's state is lost. The rule is enforced (`react-refresh/
 * only-export-components`, and this repo lints at `--max-warnings 0`), so the context and its
 * hook live beside the component rather than in it. The same reason `app/site-context-value`
 * and `app/org-context-value` are separate from the providers that fill them.
 */
import { createContext, useContext } from 'react';

export const DrawerCloseContext = createContext<(() => void) | null>(null);

export function useDrawerClose(): () => void {
  const close = useContext(DrawerCloseContext);
  if (close === null) {
    throw new Error('useDrawerClose must be called inside a <Drawer>');
  }
  return close;
}
